/**
 * RubricsPage — Faculty list + create/edit + delete rubrics.
 * Students see the list (rubric transparency) but can't open the editor.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import {
  listRubrics, createRubric, updateRubric, deleteRubric,
} from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';

const GRADER_ROLES = ['developer', 'institution_admin', 'teacher'];

const RUBRIC_CATEGORIES = [
  { value: 'assignment',   label: 'Assignment' },
  { value: 'discussion',   label: 'Discussion' },
  { value: 'quiz',         label: 'Quiz' },
  { value: 'presentation', label: 'Presentation' },
  { value: 'exam',         label: 'Exam' },
  { value: 'other',        label: 'Other / uncategorized' },
];

const blankCriterion = () => ({
  id: '',
  name: '',
  description: '',
  max_points: 5,
  weight: 1.0,
});

const blankRubric = () => ({
  title: '',
  description: '',
  module_id: '',
  topic_id: '',
  category: 'assignment',
  criteria: [blankCriterion()],
});

const RubricEditor = ({ initial, onSave, onCancel, saving }) => {
  const [data, setData] = useState(initial || blankRubric());

  const setField = (k, v) => setData({ ...data, [k]: v });
  const setCriterion = (i, k, v) => {
    const next = [...data.criteria];
    next[i] = { ...next[i], [k]: v };
    setData({ ...data, criteria: next });
  };
  const addCriterion = () =>
    setData({ ...data, criteria: [...data.criteria, blankCriterion()] });
  const removeCriterion = (i) => {
    if (data.criteria.length <= 1) return;
    setData({ ...data, criteria: data.criteria.filter((_, idx) => idx !== i) });
  };

  const totalMax = data.criteria.reduce(
    (sum, c) => sum + (Number(c.max_points) || 0) * (Number(c.weight) || 0),
    0,
  );

  const canSave =
    data.title.trim().length > 0 &&
    data.criteria.length > 0 &&
    data.criteria.every((c) => c.name.trim().length > 0);

  return (
    <div className="card p-6 mb-6" data-testid="rubric-editor">
      <h2 className="text-xl font-bold mb-3">{initial ? 'Edit Rubric' : 'New Rubric'}</h2>
      <label className="block text-xs font-semibold text-gray-700 mb-1">Title</label>
      <input
        className="input-field mb-3"
        value={data.title}
        onChange={(e) => setField('title', e.target.value)}
        placeholder="e.g., Module 1 Concept Check"
        data-testid="rubric-title-input"
      />
      <label className="block text-xs font-semibold text-gray-700 mb-1">Description</label>
      <textarea
        className="input-field mb-3"
        rows={2}
        value={data.description}
        onChange={(e) => setField('description', e.target.value)}
        placeholder="Brief overview of what this rubric evaluates"
        data-testid="rubric-description-input"
      />
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Category (for gradebook)</label>
          <select
            className="input-field"
            value={data.category || 'other'}
            onChange={(e) => setField('category', e.target.value)}
            data-testid="rubric-category-select"
          >
            {RUBRIC_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Scope: module_id (optional)</label>
          <input
            className="input-field"
            value={data.module_id || ''}
            onChange={(e) => setField('module_id', e.target.value)}
            placeholder="e.g., module_1"
            data-testid="rubric-module-input"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Scope: topic_id (optional)</label>
          <input
            className="input-field"
            value={data.topic_id || ''}
            onChange={(e) => setField('topic_id', e.target.value)}
            placeholder="leave empty for course-wide rubric"
            data-testid="rubric-topic-input"
          />
        </div>
      </div>

      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-gray-800">Criteria</h3>
        <div className="text-sm text-gray-500" data-testid="rubric-total-max">
          Weighted total max: <span className="font-mono font-bold">{totalMax.toFixed(2)}</span>
        </div>
      </div>

      {data.criteria.map((c, i) => (
        <div key={i} className="border border-gray-200 rounded p-3 mb-2" data-testid={`criterion-row-${i}`}>
          <div className="grid grid-cols-12 gap-2">
            <input
              className="input-field col-span-4"
              placeholder="Criterion name (required)"
              value={c.name}
              onChange={(e) => setCriterion(i, 'name', e.target.value)}
              data-testid={`criterion-name-${i}`}
            />
            <input
              className="input-field col-span-5"
              placeholder="Description (what good looks like)"
              value={c.description}
              onChange={(e) => setCriterion(i, 'description', e.target.value)}
              data-testid={`criterion-description-${i}`}
            />
            <input
              type="number"
              step="0.5"
              className="input-field col-span-1"
              placeholder="Max"
              value={c.max_points}
              onChange={(e) => setCriterion(i, 'max_points', e.target.value)}
              data-testid={`criterion-max-${i}`}
            />
            <input
              type="number"
              step="0.1"
              className="input-field col-span-1"
              placeholder="Wt"
              value={c.weight}
              onChange={(e) => setCriterion(i, 'weight', e.target.value)}
              data-testid={`criterion-weight-${i}`}
            />
            <button
              type="button"
              onClick={() => removeCriterion(i)}
              disabled={data.criteria.length <= 1}
              className="col-span-1 text-red-500 hover:text-red-700 text-sm disabled:opacity-30"
              data-testid={`criterion-remove-${i}`}
            >
              ✕
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={addCriterion}
        className="btn-outline text-sm"
        data-testid="rubric-add-criterion-btn"
      >
        + Add criterion
      </button>

      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onCancel} className="btn-outline" data-testid="rubric-cancel-btn">
          Cancel
        </button>
        <button
          onClick={() => onSave(data)}
          disabled={!canSave || saving}
          className="btn-primary"
          data-testid="rubric-save-btn"
        >
          {saving ? 'Saving…' : initial ? 'Update' : 'Create'}
        </button>
      </div>
    </div>
  );
};

const RubricsPage = () => {
  const { user } = useAuth();
  const canGrade = user && GRADER_ROLES.includes(user.role);
  const [rubrics, setRubrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | rubric obj
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const refresh = () =>
    listRubrics()
      .then(setRubrics)
      .finally(() => setLoading(false));

  useEffect(() => {
    refresh();
  }, []);

  const handleSave = async (data) => {
    setSaving(true);
    setError('');
    try {
      if (editing && editing.id) {
        await updateRubric(editing.id, data);
      } else {
        await createRubric(data);
      }
      setEditing(null);
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
     
    if (!window.confirm('Delete this rubric? (Submissions are preserved.)')) return;
    try {
      await deleteRubric(id);
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="min-h-screen" data-testid="rubrics-page">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Rubrics</h1>
            <p className="text-gray-500 mt-1">
              {canGrade
                ? 'Define how submissions are scored. AI drafts feedback per criterion using your course material.'
                : 'View the criteria your work is graded on.'}
            </p>
          </div>
          {canGrade && !editing && (
            <button
              onClick={() => setEditing('new')}
              className="btn-primary"
              data-testid="rubrics-new-btn"
            >
              + New Rubric
            </button>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {error}
          </div>
        )}

        {editing && (
          <RubricEditor
            initial={editing === 'new' ? null : editing}
            saving={saving}
            onSave={handleSave}
            onCancel={() => setEditing(null)}
          />
        )}

        {loading ? (
          <div className="text-gray-500">Loading…</div>
        ) : rubrics.length === 0 ? (
          <div className="card p-8 text-center text-gray-500" data-testid="rubrics-empty">
            No rubrics yet.
          </div>
        ) : (
          <div className="space-y-3" data-testid="rubrics-list">
            {rubrics.map((r) => (
              <div key={r.id} className="card p-4 flex items-center justify-between" data-testid={`rubric-row-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-gray-900 truncate">{r.title}</div>
                  <div className="text-xs text-gray-500 mt-1 flex items-center gap-2 flex-wrap">
                    {r.category && r.category !== 'other' && (
                      <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold uppercase tracking-wider text-[10px]">
                        {r.category}
                      </span>
                    )}
                    <span>{r.criteria.length} criteria · weighted max{' '}
                    <span className="font-mono">{r.total_max_points}</span>
                    {r.module_id ? <> · {r.module_id}</> : null}</span>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Link
                    to={`/rubrics/${r.id}/submissions`}
                    className="btn-outline text-sm"
                    data-testid={`rubric-submissions-btn-${r.id}`}
                  >
                    Submissions
                  </Link>
                  {canGrade && (
                    <>
                      <button
                        onClick={() => setEditing(r)}
                        className="btn-outline text-sm"
                        data-testid={`rubric-edit-btn-${r.id}`}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(r.id)}
                        className="btn-outline text-sm text-red-600 border-red-200 hover:bg-red-50"
                        data-testid={`rubric-delete-btn-${r.id}`}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default RubricsPage;
